import React from "react";
import { 
  Card, 
  CardContent, 
  CardMedia, 
  Typography, 
  Button, 
  Grid, 
  Box, 
  Container, 
  Chip, 
  Divider,
  Stack
} from "@mui/material";
import { 
  DirectionsCar, 
  DoorFront, 
  EventSeat, 
  AcUnit, 
  Star, 
  Phone, 
  VerifiedUser,
  AccessTime 
} from "@mui/icons-material";
import { motion } from "framer-motion"; // Imported for animations
import { useNavigate } from "react-router-dom";

// --- Data (Using your specific images) ---
const cars = [
  { category: "Economy", name: "Suzuki Alto", price: "₹25/km", image: "https://img.gaadibazaar.in/uv-images/4340089/conversions/319ef386-8fe1-4924-b7db-19a9084be2ac-vdp.webp", type: "Sedan", doors: "4 Doors", seats: "5 Seats", ac: "AC / Heater" },
  { category: "Leisure", name: "Suzuki Dzire", price: "₹25/km", image: "https://brothertaxiservices.com/images/home-cars/swift.jpg", type: "Sedan", doors: "4 Doors", seats: "5 Seats", ac: "AC / Heater" },
  { category: "Family", name: "Toyota Innova", price: "₹25/km", image: "https://shimlamanalitaxiservice.co.in/wp-content/uploads/2025/08/WhatsApp-Image-2025-07-15-at-15.50.11_d57ff2c3-e1752575171415-r8sk2is1tkbb6ixal2hn8vbd0z5nwq26tfujhpfnrk.webp", type: "SUV", doors: "4 Doors", seats: "8 Seats", ac: "AC / Heater" },
  { category: "Group", name: "Tempo Traveller", price: "₹25/km", image: "https://brothertaxiservices.com/images/home-cars/tempo.jpg", type: "Traveller", doors: "2 Doors", seats: "12+ Seats", ac: "AC / Heater" },
  { category: "Family", name: "Ertiga", price: "₹25/km", image: "https://brothertaxiservices.com/images/home-cars/Ertiga.jpg", type: "SUV", doors: "4 Doors", seats: "8 Seats", ac: "AC / Heater" },
  { category: "Off-Road", name: "Jeep 4 x 4", price: "₹25/km", image: "https://brothertaxiservices.com/images/home-cars/jeep.jpg", type: "SUV", doors: "4 Doors", seats: "5 Seats", ac: "AC / Heater" },
];

// Animation Variants for Framer Motion
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { duration: 0.8, ease: "easeOut" } 
  }
};

const TaxiComponent = () => {
  const navi = useNavigate();

  const handleBook = () => {
    navi('/taxibooking');
  };

  return (
    <Box sx={{ backgroundColor: "#F8F9FA", minHeight: "100vh" }}>
      
      {/* --- HERO SECTION WITH ANIMATED TEXT --- */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          aspectRatio: { xs: "4 / 5", md: "16 / 7" },
          backgroundImage: `
            linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.6)),
            url('https://taxidharamshala.in/wp-content/uploads/2023/09/Affordable-taxi-service-in-dharamshala.jpeg')
          `,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          display: "flex",
          alignItems: "center",
          color: "#fff",
          textAlign: "center"
        }}
      >
        <Container maxWidth="lg">
          {/* Animated Wrapper */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <Typography 
              variant="overline" 
              sx={{ color: "#FFD046", fontWeight: 700, letterSpacing: 3, fontSize: "1rem" }}
            >
              Himachal Tours with Ajju
            </Typography>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            transition={{ delay: 0.2 }}
          >
            <Typography 
              variant="h3" 
              component="h1" 
              fontWeight="800" 
              sx={{ mt: 1, mb: 2, fontSize: { xs: "2.2rem", md: "3.5rem" } }}
            >
              YOUR JOURNEY, <span style={{ color: "#FFD046" }}>Our Priority</span>
            </Typography>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            transition={{ delay: 0.4 }}
          >
            <Typography 
              variant="body1" 
              sx={{ maxWidth: "600px", mx: "auto", mb: 4, opacity: 0.9, fontSize: "1.1rem" }}
            >
              Reliable, Safe, and Comfortable transportation. We provide 24/7 service with transparent pricing.
            </Typography>
          </motion.div>
          
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            transition={{ delay: 0.6 }}
          >
            <Button 
              variant="contained" 
              startIcon={<Phone />}
              sx={{ 
                backgroundColor: "#FFD046", 
                color: "#091C47", 
                px: 4, 
                py: 1.5, 
                borderRadius: "50px", 
                fontWeight: 700, 
                fontSize: "1rem",
                boxShadow: "0 4px 15px rgba(255, 208, 70, 0.4)",
                "&:hover": { backgroundColor: "#e6bc40" }
              }}
              href="tel:+916230206562"
            >
              Call Anytime +91 - 6230206562
            </Button>
          </motion.div>
        </Container>
      </Box>

      {/* --- TRUST BADGES --- */}
      <Box sx={{ backgroundColor: "white", py: 3, borderBottom: "1px solid #eee" }}>
        <Container maxWidth="lg">
          <Stack 
            direction={{ xs: 'column', md: 'row' }} 
            spacing={4} 
            justifyContent="center" 
            alignItems="center"
          >
            <Chip 
              icon={<VerifiedUser style={{color: '#091C47'}} />} 
              label="Professional Drivers" 
              sx={{ fontSize: "0.9rem", fontWeight: 500, padding: "8px 12px" }} 
            />
            <Chip 
              icon={<AccessTime style={{color: '#091C47'}} />} 
              label="24/7 Available" 
              sx={{ fontSize: "0.9rem", fontWeight: 500, padding: "8px 12px" }} 
            />
            <Chip 
              icon={<DirectionsCar style={{color: '#091C47'}} />} 
              label="Well Maintained" 
              sx={{ fontSize: "0.9rem", fontWeight: 500, padding: "8px 12px" }} 
            />
          </Stack>
        </Container>
      </Box>

      {/* --- CAR GRID SECTION WITH ZOOM EFFECT --- */}
      <Box sx={{ py: 8, px: 2 }}>
        <Container maxWidth="xl">
          
          {/* Section Header */}
          <Box textAlign="center" mb={6}>
            <Typography 
              variant="h4" 
              fontWeight="bold" 
              sx={{ color: "#091C47", mb: 1 }}
            >
              What We're Offering
            </Typography>
            <Divider 
              sx={{ 
                width: "60px", 
                height: "4px", 
                backgroundColor: "#FFD046", 
                mx: "auto", 
                borderRadius: "2px" 
              }} 
            />
          </Box>

          <Grid container spacing={4} justifyContent="center">
            {cars.map((car, index) => (
              <Grid item key={index} xs={12} sm={6} md={4} lg={4}>
                <Card 
                  sx={{ 
                    borderRadius: "20px", 
                    overflow: "hidden", 
                    boxShadow: "0 10px 40px rgba(0,0,0,0.06)", 
                    transition: "all 0.3s ease-in-out",
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    border: '1px solid rgba(0,0,0,0.02)',
                    "&:hover": { 
                      transform: "translateY(-5px)", 
                      boxShadow: "0 20px 50px rgba(9, 28, 71, 0.15)" 
                    }
                  }}
                >
                  {/* Image with Top Badge & Zoom Effect */}
                  <Box sx={{ position: 'relative', overflow: 'hidden' }}>
                    <CardMedia
                      component="img"
                      height="220"
                      image={car.image}
                      alt={car.name}
                      sx={{ 
                        objectFit: "cover",
                        transition: "transform 0.6s ease", // Smooth zoom transition
                        "&:hover": { 
                          transform: "scale(1.1)" // Zoom in 10% on hover
                        }
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 16,
                        left: 16,
                        backgroundColor: "#091C47",
                        color: "#fff",
                        px: 2,
                        py: 0.5,
                        borderRadius: "6px",
                        fontSize: "0.75rem",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        boxShadow: 2
                      }}
                    >
                      {car.category}
                    </Box>
                  </Box>

                  <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    {/* Title & Rating */}
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                      <Typography variant="h6" fontWeight="800" color="#091C47">
                        {car.name}
                      </Typography>
                      <Box display="flex" alignItems="center" color="#FFD046">
                        <Star sx={{ fontSize: 16 }} />
                        <Typography variant="caption" fontWeight="bold" sx={{ ml: 0.5, color: "#666" }}>4.8</Typography>
                      </Box>
                    </Box>

                    {/* Price */}
           
                    {/* Features Grid */}
                    <Grid container spacing={1.5} sx={{ mb: 3, backgroundColor: "#F8F9FA", p: 2, borderRadius: 2 }}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={1}>
                          <DoorFront sx={{ fontSize: 16, color: "#091C47" }} /> {car.doors}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={1}>
                          <EventSeat sx={{ fontSize: 16, color: "#091C47" }} /> {car.seats}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={1}>
                          <AcUnit sx={{ fontSize: 16, color: "#091C47" }} /> {car.ac}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={1}>
                          <DirectionsCar sx={{ fontSize: 16, color: "#091C47" }} /> {car.type}
                        </Typography>
                      </Grid>
                    </Grid>

                    {/* Split Button Action */}
                    <Box sx={{ mt: "auto", display: "flex", height: "45px" }}>
                      <Button
                        fullWidth
                        variant="outlined"
                        sx={{
                          borderRadius: "25px 0 0 25px",
                          borderColor: "#E0E0E0",
                          color: "#555",
                          fontWeight: 600,
                          textTransform: "none",
                          "&:hover": { backgroundColor: "#f5f5f5", borderColor: "#bbb" }
                        }}
                      >
                        View Details
                      </Button>
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={handleBook}
                        sx={{
                          borderRadius: "0 25px 25px 0",
                          backgroundColor: "#091C47",
                          color: "#fff",
                          fontWeight: 700,
                          textTransform: "none",
                          boxShadow: "0 4px 10px rgba(9, 28, 71, 0.2)",
                          "&:hover": { backgroundColor: "#0f2d6b" }
                        }}
                      >
                        BOOK NOW
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </Box>
    );
    };

export default TaxiComponent;